import React from "react";
import { motion } from "framer-motion";

export const cx = (...a) => a.filter(Boolean).join(" ");
const GOLD = "#C7A668";
const INK = "#F2EFE8";

export function Overline({ children, gold }) {
  return (
    <span className="font-mono text-xs tracking-[0.18em] uppercase" style={{ color: gold === false ? "#9DA1A8" : GOLD }}>
      {children}
    </span>
  );
}

export function Field({ label, hint, required, children }) {
  return (
    <div className="space-y-2">
      <label className="block font-mono text-[11px] tracking-[0.12em] uppercase" style={{ color: "#7d818a" }}>
        {label} {required && <span style={{ color: GOLD }}>*</span>}
      </label>
      {children}
      {hint && <p className="text-xs font-light" style={{ color: "#6c7077" }}>{hint}</p>}
    </div>
  );
}

export function TextInput({ testid, ...p }) {
  return <input data-testid={testid} className="input-lux" {...p} />;
}
export function TextArea({ testid, ...p }) {
  return <textarea data-testid={testid} rows={3} className="input-lux" style={{ resize: "none" }} {...p} />;
}
export function Select({ testid, options, value, onChange, placeholder }) {
  return (
    <select data-testid={testid} className="input-lux" value={value || ""} onChange={(e) => onChange(e.target.value)}>
      <option value="" disabled>{placeholder || "—"}</option>
      {options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
    </select>
  );
}

export function Pills({ testidPrefix, options, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2.5">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value} type="button"
            data-testid={testidPrefix ? `${testidPrefix}-${o.value}` : undefined}
            onClick={() => onChange(o.value)}
            className="px-4 py-2.5 text-sm font-light transition-all duration-300"
            style={{
              borderRadius: 4,
              border: `1px solid ${active ? GOLD : "rgba(255,255,255,.12)"}`,
              color: active ? GOLD : "#9DA1A8",
              background: active ? "rgba(199,166,104,.1)" : "transparent",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function OptionCard({ active, gold, onClick, title, sub, badge, testid }) {
  return (
    <button
      type="button" data-testid={testid} onClick={onClick}
      className="text-left p-6 w-full tbcard"
      style={{
        borderRadius: 5,
        border: `1px solid ${active ? GOLD : "rgba(255,255,255,.1)"}`,
        boxShadow: active ? "0 0 26px rgba(199,166,104,.18)" : undefined,
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <h4 className="text-lg font-display" style={{ color: INK, fontWeight: 500 }}>{title}</h4>
        {badge && <span className="font-mono text-[10px] tracking-wider uppercase px-2 py-1" style={{ borderRadius: 3, background: "rgba(199,166,104,.16)", color: GOLD }}>{badge}</span>}
      </div>
      {sub && <p className="text-sm font-light mt-2 leading-relaxed" style={{ color: "#9DA1A8" }}>{sub}</p>}
    </button>
  );
}

export function Checkbox({ checked, onChange, children, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer group" data-testid={testid} onClick={() => onChange(!checked)}>
      <span
        className="mt-0.5 h-4 w-4 shrink-0 flex items-center justify-center transition-all"
        style={{ borderRadius: 3, border: `1px solid ${checked ? GOLD : "rgba(255,255,255,.3)"}`, background: checked ? GOLD : "transparent" }}
      >
        {checked && <span className="text-[10px]" style={{ color: "#0a0a0a" }}>✓</span>}
      </span>
      <span className="text-sm font-light leading-snug" style={{ color: "#9DA1A8" }}>{children}</span>
    </label>
  );
}

export function ProgressBar({ step, total }) {
  return (
    <div className="w-full overflow-hidden" style={{ height: 2, background: "rgba(255,255,255,.1)" }}>
      <motion.div
        initial={false}
        animate={{ width: `${(step / total) * 100}%` }}
        transition={{ duration: 0.7, ease: [0.33, 1, 0.68, 1] }}
        style={{ height: "100%", background: "linear-gradient(90deg,#C7A668,#DCC79B)", boxShadow: "0 0 12px rgba(199,166,104,.7)" }}
      />
    </div>
  );
}

export function StepWrap({ k, children }) {
  return (
    <motion.div
      key={k}
      initial={{ opacity: 0, y: 16, filter: "blur(8px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      exit={{ opacity: 0, y: -10, filter: "blur(6px)" }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="space-y-8"
    >
      {children}
    </motion.div>
  );
}

export function PrimaryBtn({ children, gold, className, testid, ...p }) {
  return (
    <button data-testid={testid} className={cx("btn", gold ? "btn-gold" : "btn-primary", className)} {...p}>
      {children}
    </button>
  );
}

export function GhostBtn({ children, testid, ...p }) {
  return (
    <button data-testid={testid} className="transition-colors tracking-wide text-sm font-mono uppercase" style={{ color: "#7d818a", background: "transparent", border: "none", cursor: "pointer" }} onMouseOver={(e) => (e.currentTarget.style.color = "#C7A668")} onMouseOut={(e) => (e.currentTarget.style.color = "#7d818a")} {...p}>
      {children}
    </button>
  );
}
